import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import logo from "../../../../HomePage/img/logo.png"; // ✅ your local image

export default function CSS_Masking() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎭 Advanced CSS: Masking</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS <code>mask</code> and <code>-webkit-mask</code> properties allow you
        to control which parts of an element are visible. Masks can be images,
        gradients, or shapes, creating advanced visual effects without extra
        markup or JavaScript.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>mask-image</code> defines the image used for masking.
          </li>
          <li>
            <code>mask-repeat</code>, <code>mask-position</code>,{" "}
            <code>mask-size</code> control placement and scaling.
          </li>
          <li>
            <code>mask-mode</code> can be <code>alpha</code> or{" "}
            <code>luminance</code>.
          </li>
          <li>
            WebKit prefix <code>-webkit-mask</code> is required for
            Safari/Chrome support.
          </li>
          <li>Combine with gradients for smooth fade effects or shapes.</li>
        </ul>
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">✨ Masking Examples</h2>

        {/* Image Mask */}
        <h3>1. Image Mask (Logo)</h3>
        <CodeCopyBlock
          code={`div {
  width: 300px;
  height: 200px;
  background: #3b82f6;
  mask-image: url('logo.png');
  mask-size: cover;
  mask-repeat: no-repeat;
  -webkit-mask-image: url('logo.png');
  -webkit-mask-size: cover;
  -webkit-mask-repeat: no-repeat;
}`}
        />
        <div
          style={{
            width: "350px",
            height: "200px",
            backgroundColor: "#3b82f6",
            maskImage: `url(${logo})`,
            maskSize: "cover",
            maskRepeat: "no-repeat",
            WebkitMaskImage: `url(${logo})`,
            WebkitMaskSize: "cover",
            WebkitMaskRepeat: "no-repeat",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />
        {/* Image + Text Mask Example */}
        <h3>2. Image Mask with Text/Logo</h3>
        <p>
          Here we apply the <b>logo mask</b> to the scenic image of Cinque
          Terre:
        </p>
        <CodeCopyBlock
          code={`div {
  width: 400px;
  height: 250px;
  background: url('cinque.jpg') no-repeat center/cover;
  mask-image: url('logo.png');
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: url('logo.png');
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
}`}
        />

        <div
          style={{
            width: "400px",
            height: "250px",
            backgroundImage:
              "url('https://www.w3schools.com/css/img_5terre.jpg')", // ✅ scenic photo
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: `url(${logo})`, // ✅ your logo.png as mask
            maskRepeat: "no-repeat",
            maskPosition: "center",
            maskSize: "contain",
            WebkitMaskImage: `url(${logo})`,
            WebkitMaskRepeat: "no-repeat",
            WebkitMaskPosition: "center",
            WebkitMaskSize: "contain",
            marginBottom: "1rem",
            borderRadius: "12px",
          }}
        />

        {/* Gradient Mask */}
        <h3>3. Gradient Mask</h3>

        <p>
          A gradient can be used as a mask to create smooth fade effects. In
          this example, the bottom part is fully visible while the top fades
          out.
        </p>
        <CodeCopyBlock
          code={`div {
  width: 400px;
  height: 250px;
  background: url('cinque.jpg') no-repeat center/cover;
  mask-image: linear-gradient(to top, black, transparent);
  -webkit-mask-image: linear-gradient(to top, black, transparent);
}`}
        />

        <div
          style={{
            width: "400px",
            height: "250px",
            backgroundImage:
              "url('https://www.w3schools.com/css/img_5terre.jpg')", // scenic Cinque Terre photo
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "linear-gradient(to top, black, transparent)",
            WebkitMaskImage: "linear-gradient(to top, black, transparent)",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />

        {/* Circle Mask */}
        <h3>4. Shape Mask (Circle)</h3>

        <p>
          We can create a circular mask using a <code>radial-gradient</code>.
          The black area is visible while the transparent area hides the rest of
          the element.
        </p>
        <CodeCopyBlock
          code={`div {
  width: 250px;
  height: 250px;
  background: url('cinque.jpg') no-repeat center/cover;
  mask-image: radial-gradient(circle, black 70%, transparent 100%);
  -webkit-mask-image: radial-gradient(circle, black 70%, transparent 100%);
}`}
        />

        <div
          style={{
            width: "250px",
            height: "250px",
            backgroundImage:
              "url('https://www.w3schools.com/css/img_5terre.jpg')", // scenic Cinque Terre photo
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage: "radial-gradient(circle, black 70%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(circle, black 70%, transparent 100%)",
            borderRadius: "12px",
            marginBottom: "1rem",
          }}
        />
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Experiment with <code>mask-composite</code> to combine multiple
            masks.
          </li>
          <li>Use gradients for smooth fade-in/out masking effects.</li>
          <li>Combine masks with images or videos for creative overlays.</li>
          <li>
            Explore <code>clip-path</code> for additional shape masking.
          </li>
          <li>
            Animate masks using CSS transitions or keyframes for dynamic
            effects.
          </li>
        </ul>
      </div>
    </div>
  );
}
